import React from 'react'

import CopyableStateProvider from '@SonarCX/providers/CopyableStateProvider'

import MessageSkeletonV2 from '@SonarCX/components/molecules/MessageSkeletonV2'
import TaskComment from '@SonarCX/components/molecules/ChatModals/TaskModal/TaskComments/TaskComment'
import TaskCommentInput from '@SonarCX/components/atoms/TaskCommentInput'

import { mockComment } from '@SonarCX/providers/TaskAPIProvider/helpers'
import { useTaskComments } from '@SonarCX/providers/TaskCommentsProvider'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useStyles from './styles'

type CommentsProps = {
  task: LocalTask
}

const Comments: React.FC<CommentsProps> = ({ task }) => {
  const { comments, loadComments, processCommentEvent } = useTaskComments()
  const { canView } = useTaskPermissions(task)
  const { user } = useEnvironment()
  const { addComment } = useTaskAPI()

  const [loaded, setLoaded] = React.useState(false)
  const [comment, setComment] = React.useState('')

  const taskId = React.useMemo(() => task.id, [task])

  const sortedComments = React.useMemo(() => {
    const sorted = (
      comments[task.id] ? Object.values(comments[task.id]) : []
    ).sort((a: any, b: any) => +new Date(b.stamp) - +new Date(a.stamp))
    return sorted.map(comment => ({ type: 'comment', data: comment }))
  }, [comments, task.id])

  const classes = useStyles()

  React.useEffect(() => {
    const doLoad = async () => {
      if (loaded) return
      await loadComments(task.id)
      setTimeout(() => {
        setLoaded(true)
      }, 500)
    }
    doLoad()
  }, [task.id, loadComments, loaded])

  const handleSubmit = React.useCallback(async () => {
    const trimmed = comment.trim()

    if (trimmed.length) {
      const mocked = mockComment(trimmed, user?.id, taskId)
      processCommentEvent({ comment: mocked })
      setComment('')
      await addComment(taskId, trimmed)
    }
  }, [comment, user, taskId, processCommentEvent, addComment])

  return (
    <div className={classes.root}>
      {!loaded ? (
        <div className={classes.loading}>
          <MessageSkeletonV2 isYours />
          <MessageSkeletonV2 isYours={false} />
        </div>
      ) : (
        <>
          <div onClick={e => e.stopPropagation()}>
            <TaskCommentInput
              value={comment}
              onChange={setComment}
              canComment={canView}
              submit={handleSubmit}
              className={classes.input}
            />
          </div>

          {sortedComments.length === 0 ? (
            <div>No comments.</div>
          ) : (
            <div className={classes.comments}>
              <CopyableStateProvider>
                {sortedComments.map((comment: any) => (
                  <div key={comment.data.id} className={classes.comment}>
                    <TaskComment comment={comment.data} inModal disableEdit />
                  </div>
                ))}
              </CopyableStateProvider>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Comments
